import React from "react";
import { CircularProgress } from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PaymentIcon from "@mui/icons-material/Payment";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import {
  DeletePopUp,
  ErrorPanel,
  ErrorPanelHeader,
  isDeletionBlocked,
  LinkedBrokersBlock,
} from "../../../../../components/PopUp";
import { pluralize } from "../../../../../utils";
import { DeleteSavingModalProps, ImpactRow } from "./types";
import {
  ImpactList,
  ImpactItem,
  ImpactIcon,
  ImpactCount,
  SuccessPanel,
  LoadingPanel,
  FootNote,
} from "./styles";

export const DeleteSavingModal: React.FC<DeleteSavingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  savingName,
  deletionImpact,
  isLoadingImpact = false,
  isDeleting = false,
  deleteError,
}) => {
  const isBlocked = isDeletionBlocked(deletionImpact);

  const buildImpactRows = (): ImpactRow[] => {
    if (!deletionImpact) {
      return [];
    }

    const { expenseCount, incomeCount, transferCount, loanPaymentCount } =
      deletionImpact;

    const rows: ImpactRow[] = [
      {
        tone: "expense",
        icon: <TrendingDownIcon />,
        count: expenseCount,
        label: "expense",
      },
      {
        tone: "income",
        icon: <TrendingUpIcon />,
        count: incomeCount,
        label: "income transaction",
      },
      {
        tone: "transfer",
        icon: <SwapHorizIcon />,
        count: transferCount,
        label: "transfer",
      },
      {
        tone: "loan",
        icon: <PaymentIcon />,
        count: loanPaymentCount,
        label: "loan payment",
      },
    ];

    return rows.filter((row) => row.count > 0);
  };

  const renderImpactDetails = () => {
    if (isLoadingImpact) {
      return (
        <LoadingPanel
          role="status"
          aria-label="Loading deletion impact information"
        >
          <CircularProgress size={16} aria-label="Loading" />
          Loading deletion impact...
        </LoadingPanel>
      );
    }

    if (!deletionImpact) {
      return null;
    }

    if (isBlocked) {
      return <LinkedBrokersBlock brokers={deletionImpact.linkedBrokers} />;
    }

    const { totalTransactions } = deletionImpact;

    if (totalTransactions === 0) {
      return (
        <SuccessPanel role="status" aria-label="No transactions affected">
          <CheckCircleRoundedIcon aria-hidden="true" />
          No transactions will be affected by this deletion.
        </SuccessPanel>
      );
    }

    const impactRows = buildImpactRows();
    const headingText = `${totalTransactions.toLocaleString()} ${pluralize(totalTransactions, "transaction")} will be permanently impacted or deleted:`;

    return (
      <ErrorPanel
        role="alert"
        aria-label={`Warning: ${totalTransactions} transactions will be affected`}
      >
        <ErrorPanelHeader>
          <WarningAmberRoundedIcon aria-hidden="true" />
          {headingText}
        </ErrorPanelHeader>
        <ImpactList aria-label="Transaction impact breakdown">
          {impactRows.map((row) => (
            <ImpactItem key={row.tone}>
              <ImpactIcon $tone={row.tone} aria-hidden="true">
                {row.icon}
              </ImpactIcon>
              <ImpactCount>{row.count.toLocaleString()}</ImpactCount>
              {pluralize(row.count, row.label)}
            </ImpactItem>
          ))}
        </ImpactList>
      </ErrorPanel>
    );
  };

  return (
    <DeletePopUp
      isOpen={isOpen}
      setIsOpen={onClose}
      title="Delete saving"
      text={
        <>
          Do you really want to delete the <strong>{savingName}</strong> saving
          account?
          {renderImpactDetails()}
          <FootNote>
            All transactions related with this account will be kept but they
            will no longer be associated with any valid account.
          </FootNote>
          <FootNote>
            Check if you want just to deactivate this account.
          </FootNote>
        </>
      }
      handleOnDelete={onConfirm}
      isLoading={isDeleting}
      isConfirmDisabled={isBlocked}
      errorMessage={deleteError}
    />
  );
};
